import * as React from "react";
import { useMemo } from "react";
import { shallowEqual } from "react-redux";

import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { addInstance, LastInstanceBehaviour, removeInstance } from "../../../Engine/ActionCreators/AllActionCreators";
import { withDeferredInit } from "../../withDeferredInit";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import { useTypedDispatch, useTypedSelector } from "../../../../../Common/UseTypedSelector";
import { useMultiline } from "../../../Engine/Core/Filtering/UseMultiline";
import { withTestPageObjectRegistration } from "../../Services/TestPageObjects/PageObjectExposure";
import { createHocDisplayName } from "../../TypingUtils";

import { Table2Multirow, Table2MultirowProps } from "./Table2Multirow";

type Table2MultirowContainerProps = Omit<Table2MultirowProps, "filteredUniqKey"> & {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    deferredInit?: boolean;
    filteredUniqKey?: string;
    defaultFilter?: FilterExpression;
    debounceDisable?: boolean;
};

function withTable2MultirowWrapper(Component: typeof Table2MultirowWithDeferred) {
    const WithTable2MultirowWrapper = (props: Table2MultirowContainerProps & { children: React.ReactNode[] }) => {
        const {
            bindingPath,
            defaultFilter,
            filteredUniqKey = "default",
            debounceDisable,
            viewModelSelect,
            deferredInit,
        } = props;
        const dispatch = useTypedDispatch();
        const actions = useMemo(
            () => ({
                onAddInstance: () => {
                    dispatch(addInstance(bindingPath, undefined, undefined, undefined, undefined, filteredUniqKey));
                },
                onRemoveInstance: (instancesToRemove: string) => {
                    dispatch(removeInstance(bindingPath, instancesToRemove, LastInstanceBehaviour.Clear));
                },
            }),
            [dispatch, bindingPath, filteredUniqKey]
        );
        const childrenCount = useTypedSelector(
            state => viewModelSelect.children(state, bindingPath, []).length,
            shallowEqual
        );

        const { displayedInstances } = useMultiline({
            bindingPath: bindingPath,
            filteredUniqKey: filteredUniqKey,
            useShortDebounce: debounceDisable ?? false,
            instancesPerPage: 1,
            storeFiltersWithinSession: false,
            defaultFilter: defaultFilter,
        });

        return (
            <Component
                {...props}
                bindingPath={props.bindingPath}
                childrenCount={childrenCount}
                deferredInit={deferredInit}
                instances={displayedInstances}
                filteredUniqKey={filteredUniqKey}
                {...actions}>
                {props.children}
            </Component>
        );
    };
    WithTable2MultirowWrapper.displayName = createHocDisplayName(Table2Multirow, "Table2MultirowWrapper");
    return WithTable2MultirowWrapper;
}

const Table2MultirowWithDeferred = withDeferredInit(
    withTestPageObjectRegistration(Table2Multirow, x => [`${x.bindingPath.toLegacyPath()}/${x.filteredUniqKey}`])
);

export const Table2MultirowContainer = withResolvePaths(
    withViewModelSelect(withTable2MultirowWrapper(Table2MultirowWithDeferred))
);
